import { memo } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { severityColor, cellHeadline, type CellSummary } from '../landscapeStatus';

interface LandscapeCellProps {
  cell: CellSummary | undefined;
  onClick: () => void;
}

const LandscapeCell = memo<LandscapeCellProps>(({ cell, onClick }) => {
  if (!cell || cell.total === 0) {
    return (
      <Box
        sx={{
          minHeight: (theme) => theme.spacing(7),
          borderRadius: 1,
          border: '1px dashed',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" color="text.disabled">—</Typography>
      </Box>
    );
  }

  const color = severityColor(cell.severity);
  const headline = cellHeadline(cell);
  // Primary count: attention if any, else in-sync count.
  const primary = cell.attention > 0 ? `${cell.attention}/${cell.total}` : `${cell.inSync}/${cell.total}`;

  return (
    <Tooltip
      title={`${cell.total} deployment${cell.total !== 1 ? 's' : ''} · ${headline}${cell.blocked > 0 ? ` · ${cell.blocked} blocked` : ''}`}
      arrow
    >
      <Box
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
        sx={{
          minHeight: (theme) => theme.spacing(7),
          borderRadius: 1,
          cursor: 'pointer',
          px: 1,
          py: 0.75,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 0.25,
          position: 'relative',
          borderLeft: '3px solid',
          borderLeftColor: color === 'default' ? 'divider' : `${color}.main`,
          bgcolor: (theme) => color === 'default'
            ? theme.palette.action.hover
            : alpha(theme.palette[color].main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
          transition: 'background-color 120ms',
          '&:hover': {
            bgcolor: (theme) => color === 'default'
              ? theme.palette.action.selected
              : alpha(theme.palette[color].main, theme.palette.mode === 'dark' ? 0.32 : 0.2),
          },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
        }}
      >
        {cell.blocked > 0 && (
          <Box
            sx={{
              position: 'absolute', top: 0.5, right: 0.5,
              width: (theme) => theme.spacing(1), height: (theme) => theme.spacing(1),
              borderRadius: '50%', bgcolor: 'error.main',
            }}
          />
        )}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1 }}>
          {primary}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1 }} noWrap>
          {headline}
        </Typography>
      </Box>
    </Tooltip>
  );
});

LandscapeCell.displayName = 'LandscapeCell';
export default LandscapeCell;
